import type { PreferencesResponse } from "./types";

export type JourneyId = "setup" | "test" | "preferences" | "account" | "help";
export type ReadinessSeverity = "ready" | "pending" | "blocked";

export interface ReadinessBlocker {
  code: string;
  label: string;
  actionTarget: JourneyId | "disclosures" | "contact" | "verification" | "automatic";
  severity: ReadinessSeverity;
}

export interface SetupReadiness {
  linked: boolean;
  disclosuresAccepted: boolean;
  contactUsable: boolean;
  automaticEnabled: boolean;
  blockers: ReadinessBlocker[];
  primaryAction: string;
  readyForAutomaticProcessing(): boolean;
  readyForManualTesting(): boolean;
}

export function deriveSetupReadiness(preferences: PreferencesResponse): SetupReadiness {
  const linked = preferences.strava_authenticated;
  const disclosuresAccepted =
    preferences.privacy_disclosure_accepted &&
    preferences.privacy_policy_accepted &&
    preferences.provider_terms_accepted &&
    preferences.delivery_consent_accepted &&
    preferences.age_eligibility_confirmed &&
    preferences.disclosure_version.trim() !== "";
  const contactUsable =
    preferences.masked_destination.trim() !== "" && preferences.verification_status === "verified";
  const automaticEnabled = preferences.integration_enabled;
  const blockers: ReadinessBlocker[] = [];

  if (!linked) {
    blockers.push({
      code: "strava_not_linked",
      label: "Connect Strava to get started.",
      actionTarget: "setup",
      severity: "blocked"
    });
  }
  if (!disclosuresAccepted) {
    blockers.push({
      code: "disclosures_required",
      label: "Accept the privacy, map, and delivery terms.",
      actionTarget: "disclosures",
      severity: "blocked"
    });
  }
  if (!preferences.masked_destination) {
    blockers.push({
      code: "contact_missing",
      label: "Add where your map images should be sent.",
      actionTarget: "contact",
      severity: "blocked"
    });
  } else if (!contactUsable) {
    blockers.push({
      code: "contact_not_verified",
      label: "Verify your delivery contact.",
      actionTarget: "verification",
      severity: "pending"
    });
  }
  if (!automaticEnabled) {
    blockers.push({
      code: "automatic_disabled",
      label: "Turn on auto-processing when you are ready.",
      actionTarget: "automatic",
      severity: "pending"
    });
  }

  return {
    linked,
    disclosuresAccepted,
    contactUsable,
    automaticEnabled,
    blockers,
    primaryAction: blockers[0]?.actionTarget ?? "test",
    readyForAutomaticProcessing() {
      return linked && disclosuresAccepted && contactUsable && automaticEnabled;
    },
    readyForManualTesting() {
      return linked && disclosuresAccepted && contactUsable;
    }
  };
}
